/**
 * RecruitCards - the recruitment list as cards (docs/plans/crm-recruitment-list-plan.md Part A
 * section 4, decision 8, B4): what a phone always gets (RecruitSheet forces Cards below sm:), and the
 * Cards side of the page's Sheet / Cards switch.
 *
 * One card per lead, in the list's order after its filters and sort, carrying the sheet's fields
 * through the sheet's own cells (sheet/cells/), so a card edits exactly what a row edits, through the
 * same endpoints: the reason line first, then the lead (a real link) and the phone, the flags, then
 * the status on this study, the recruiter, the attempts with the attempt menu and the callback, the
 * sticky note, and what is known about them (the last touch, how they came in, what this study's own
 * form said about their eligibility, the study's answer columns).
 *
 * Each card has its tick box, "Select all" ticks every lead on the list, and the bulk bar
 * (sheet/BulkBar: Set recruiter, Set status) works on the ticked cards. RecruitSheet mounts that bar
 * itself only in Sheet view, so this is the one bar in Cards view.
 *
 * A lead closed on this study stays where it is until a refresh (decision 6), its words greyed and
 * its controls at full strength: an opacity (or transform, or sticky) ancestor would fade or trap
 * the reason and randomized-elsewhere modals the controls open in place, and misplace the attempt
 * menu, so nothing that wraps a control ever carries one (the SheetGrid rule). The lead opens like a
 * real link: the name is a RowLink, and a tap on the card's head opens the lead (Ctrl, Cmd and the
 * middle button open a new tab); the controls keep their own clicks. A long list is windowed with
 * useWindowedSlice (the page scrolls; the spacers are this list's own <li>s).
 */
import { memo, useCallback, useMemo } from 'react';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import CallButton from '../components/CallButton';
import { isActiveSnooze } from '../components/SnoozeButton';
import { RowLink, eligibilityBadge, useRowNav } from '../components/shared';
import { dateTime, formatPhone, prettySlug, relTime } from '../lib';
import { contactName } from '../types';
import { useWindowedSlice } from '../useWindowedSlice';
import { NO_STUDY_FORM_TITLE, STUDY_ELIGIBILITY_TITLE, isTerminalStatus } from './recruitLogic';
import BulkBar from './sheet/BulkBar';
import AnswerCell from './sheet/cells/AnswerCell';
import AttemptsCell from './sheet/cells/AttemptsCell';
import CallbackCell from './sheet/cells/CallbackCell';
import FlagsCell from './sheet/cells/FlagsCell';
import LastTouchCell from './sheet/cells/LastTouchCell';
import NoteCell from './sheet/cells/NoteCell';
import OwnerCell from './sheet/cells/OwnerCell';
import ReasonCell from './sheet/cells/ReasonCell';
import SelectCell, { CHECKBOX_CLS } from './sheet/cells/SelectCell';
import StatusCell from './sheet/cells/StatusCell';
import type { RecruitRow, RecruitRowChangeHandler, RecruitSelectHandler, RecruitStudy } from './types';

export interface RecruitCardsProps {
  /** The rows in the sheet's order, after its filters and sort. */
  rows: readonly RecruitRow[];
  /** The study the list is for (RecruitRowsResponse.study). */
  study: RecruitStudy;
  /** The ticked rows, by contact id (the bulk bar's selection). */
  selection: ReadonlySet<string>;
  /** A card changed a row (status, recruiter, note, a logged attempt): apply `update` to the current row. */
  onRowChange: RecruitRowChangeHandler;
  /** Tick or untick one row. */
  onSelect: RecruitSelectHandler;
}

// ---------------------------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------------------------

/**
 * Where a lead came in on this study, in words: the lead's own source for this study, else how
 * they joined its list. KEEP IN STEP with sheet/SheetGrid.tsx cameInSource (module-private there),
 * so a card and a row never word the same lead two ways.
 */
const JOINED_BY: Readonly<Record<string, string>> = {
  lead: 'Form',
  form: 'Form',
  import: 'Import',
  recruiter: 'Added by hand',
  pivot: 'Moved from another study',
  ctms: 'CTMS',
  agent: 'Texting agent',
  backfill: 'Backfill',
  trigger: 'Automatic',
};

export function cameInSource(row: RecruitRow, study: Pick<RecruitStudy, 'slug'>): string {
  const link = (row.contact.studies || []).find((s) => s.study_slug === study.slug && s.lead_source);
  if (link?.lead_source) return prettySlug(link.lead_source);
  const source = row.membership.source;
  if (source && Object.prototype.hasOwnProperty.call(JOINED_BY, source)) return JOINED_BY[source];
  return source ? prettySlug(source) : '';
}

/** Anything the Flags cell would show (it renders a bare dash otherwise, which a card leaves out). */
export function hasFlags(row: RecruitRow): boolean {
  const f = row.flags;
  const agent = row.agent?.status;
  return !!(
    f && (f.randomized_elsewhere || f.opted_out || f.do_not_contact || f.archived || f.focus_study
      || (f.snoozed_until && isActiveSnooze(f.snoozed_until)))
  ) || agent === 'active' || agent === 'awaiting_review';
}

/** The study's answer columns this lead carries, in the order the server sent them. */
function answerKeys(row: RecruitRow): string[] {
  const answers = row.answers;
  return answers && typeof answers === 'object' ? Object.keys(answers) : [];
}

/** The sheet's call button, compact (44px on phones). */
const CALL_BTN_CLS =
  'inline-flex min-h-[44px] flex-shrink-0 items-center gap-1 rounded-md border border-gray-300 px-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:h-7 sm:min-h-0';

const LABEL_CLS = 'text-[10px] font-semibold uppercase tracking-widest text-gray-600 dark:text-gray-400';

const stop = (e: ReactMouseEvent) => e.stopPropagation();

/**
 * One labelled field. `inline` puts the label beside the control on a phone (a 44px row) and above
 * it from sm: up; otherwise the label sits above the value everywhere.
 */
function Field({ label, children, inline = false, className = '' }: {
  label: string;
  children: ReactNode;
  inline?: boolean;
  className?: string;
}) {
  return (
    <div className={`min-w-0 ${inline ? 'flex items-center justify-between gap-3 sm:block' : ''} ${className}`}>
      <dt title={label} className={`${LABEL_CLS} ${inline ? 'flex-shrink-0 sm:mb-1' : 'mb-0.5 truncate'}`}>{label}</dt>
      <dd className={`min-w-0 ${inline ? 'flex justify-end sm:block' : ''}`}>{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// One card
// ---------------------------------------------------------------------------------------------

type RowNav = ReturnType<typeof useRowNav>;

interface RecruitCardProps {
  row: RecruitRow;
  study: RecruitStudy;
  selected: boolean;
  onSelect: RecruitSelectHandler;
  onRowChange: RecruitRowChangeHandler;
  rowNav: RowNav;
}

/**
 * Memoized like the inbox's ContactCard: selection arrives as a boolean and every handler is the
 * page's stable one, so a tick or a change on one card re-renders that card only.
 */
const RecruitCard = memo(function RecruitCard({ row, study, selected, onSelect, onRowChange, rowNav }: RecruitCardProps) {
  const c = row.contact;
  const id = c.id;
  const href = `/crm/contacts/${id}`;
  const name = contactName(c);
  const closed = isTerminalStatus(row.membership.status);
  const replyOwed = row.reason?.kind === 'needs_reply';
  // Greyed words, never a greyed control (see the header).
  const words = closed ? 'opacity-60' : '';
  const source = cameInSource(row, study);
  // This study's own form only: another study's verdict never shows here.
  const elig = eligibilityBadge(c.study_eligibility ?? null);
  const keys = answerKeys(row);

  return (
    <li
      data-contact={id}
      data-closed={closed ? 'true' : undefined}
      className={`rounded-xl border p-3 shadow-sm transition-colors duration-150 sm:p-4 ${
        selected
          ? 'border-primary-400 bg-primary-50/40 ring-1 ring-primary-400 dark:border-primary-500 dark:bg-gray-800 dark:ring-primary-500'
          : closed
            ? 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60'
            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
      }`}
    >
      {/* The head: tick box, the reason first, then the lead. A tap here opens the lead. */}
      <div {...rowNav(href)} className="-m-1 flex cursor-pointer items-start gap-2 rounded-lg p-1 transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/40">
        <SelectCell row={row} selected={selected} onSelect={onSelect} className="-my-2.5 -ml-2.5 flex-shrink-0 sm:my-0 sm:ml-0 sm:mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className={words}>
            <ReasonCell row={row} study={study} className="text-sm" />
          </div>
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            <span className={`flex min-w-0 items-center gap-1.5 ${words}`}>
              <RowLink
                to={href}
                title={name}
                className={`min-w-0 truncate text-sm text-gray-900 dark:text-gray-100 ${replyOwed ? 'font-semibold' : 'font-medium'}`}
              >
                {name}
              </RowLink>
              {replyOwed && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" title="Replied, waiting on us" />}
            </span>
            {c.phone_e164 && (
              <span className="flex min-w-0 items-center gap-1.5" onClick={stop} onAuxClick={stop}>
                <span className={`truncate text-xs tabular-nums text-gray-700 dark:text-gray-200 ${words}`}>{formatPhone(c.phone_e164)}</span>
                <CallButton to={c.phone_e164} contactId={id} name={name} className={CALL_BTN_CLS} />
              </span>
            )}
          </div>
        </div>
      </div>

      {hasFlags(row) && (
        <div className="mt-2">
          <FlagsCell row={row} study={study} onRowChange={onRowChange} />
        </div>
      )}

      <div className="mt-3 space-y-3">
        {/* What a recruiter changes on the call: the same controls as the sheet's row. */}
        <dl className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2 sm:gap-y-3 xl:grid-cols-4">
          <Field label="Status" inline>
            <StatusCell row={row} study={study} onRowChange={onRowChange} />
          </Field>
          <Field label="Recruiter" inline>
            <OwnerCell row={row} onRowChange={onRowChange} />
          </Field>
          <Field label="Attempts" inline>
            <AttemptsCell row={row} study={study} onRowChange={onRowChange} />
          </Field>
          <Field label="Callback" inline>
            <CallbackCell row={row} study={study} onRowChange={onRowChange} />
          </Field>
        </dl>

        <dl>
          <Field label="Note">
            <NoteCell row={row} study={study} onRowChange={onRowChange} />
          </Field>
        </dl>

        {/* What is known about them. */}
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 xl:grid-cols-4">
          <Field label="Last touch" className={`col-span-2 sm:col-span-1 ${words}`}>
            <LastTouchCell row={row} />
          </Field>
          <Field label="Came in" className={words}>
            <div className="min-w-0 text-xs" title={dateTime(row.membership.first_considered_at)}>
              <div className="truncate text-gray-700 dark:text-gray-200">{relTime(row.membership.first_considered_at)}</div>
              {source && <div className="truncate text-gray-600 dark:text-gray-400">{source}</div>}
            </div>
          </Field>
          <Field label="Eligibility" className={words}>
            {elig
              ? <span title={STUDY_ELIGIBILITY_TITLE} className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${elig.cls}`}>{elig.label}</span>
              : <span title={NO_STUDY_FORM_TITLE} className="text-sm text-gray-400 dark:text-gray-500">—</span>}
          </Field>
          {keys.map((key) => {
            const label = row.answers[key]?.label || key;
            return (
              <Field key={key} label={label} className={words}>
                <AnswerCell row={row} questionKey={key} label={label} />
              </Field>
            );
          })}
        </dl>
      </div>
    </li>
  );
});

// ---------------------------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------------------------

export default function RecruitCards({ rows, study, selection, onRowChange, onSelect }: RecruitCardsProps) {
  const rowNav = useRowNav();
  // Card pitch: a card's height plus the stack's 12px gap (space-y-3). Inert below 100 cards.
  const slice = useWindowedSlice({ count: rows.length, gap: 12, estimatedRowHeight: 320 });

  const selectedRows = useMemo(
    () => (selection.size ? rows.filter((r) => selection.has(r.contact.id)) : []),
    [rows, selection],
  );
  const allSelected = rows.length > 0 && selectedRows.length === rows.length;
  const someSelected = selectedRows.length > 0 && !allSelected;

  // Tick or untick every lead on the list, one row at a time through the page's own handler.
  const selectAll = (on: boolean) => {
    rows.forEach((r) => {
      if (selection.has(r.contact.id) !== on) onSelect(r.contact.id, on);
    });
  };

  const clear = useCallback(() => {
    selection.forEach((contactId) => onSelect(contactId, false));
  }, [selection, onSelect]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200 sm:min-h-0">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected;
            }}
            onChange={(e) => selectAll(e.target.checked)}
            aria-label="Select every lead on the list"
            className={CHECKBOX_CLS}
          />
          Select all {rows.length.toLocaleString()}
        </label>
      </div>

      <BulkBar selected={selectedRows} study={study} onRowChange={onRowChange} onClear={clear} />

      <ul ref={slice.containerRef} className="space-y-3" aria-label="Leads on this list">
        {slice.topPad > 0 && <li aria-hidden="true" data-spacer="1" style={{ height: slice.topPad }} />}
        {rows.slice(slice.start, slice.end).map((row) => (
          <RecruitCard
            key={row.contact.id}
            row={row}
            study={study}
            selected={selection.has(row.contact.id)}
            onSelect={onSelect}
            onRowChange={onRowChange}
            rowNav={rowNav}
          />
        ))}
        {slice.bottomPad > 0 && <li aria-hidden="true" data-spacer="1" style={{ height: slice.bottomPad }} />}
      </ul>
    </div>
  );
}
